import { SVG } from 'Components';
import { SVG_TYPE } from 'Shared/enums';
import robot from 'Assets/files/robot.png';
import { useEffect, useRef, useState } from 'react';
import { MainType } from './Container';

function InstructorAIPage(props: MainType) {
  const {
    authorizedUser,
    chats,
    activeChatUuid,
    messages,
    isStreaming,
    streamBuffer,
    error,
    actions,
  } = props;

  const {
    connectWebSocket,
    disconnectWebSocket,
    fetchChats,
    createNewChat,
    createNewChatAndSend,
    loadChatHistory,
    sendChatMessage,
    clearError,
  } = actions;

  const [inputValue, setInputValue] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const recognitionRef = useRef<any>(null);

  useEffect(() => {
    if (!authorizedUser || authorizedUser === 'init') return;

    let isMounted = true;

    const setupChat = async () => {
      try {
        await connectWebSocket();
        if (isMounted) {
          fetchChats();
        }
      } catch (err) {
        console.error('[AiChat] Setup failed', err);
      }
    };

    setupChat();

    return () => {
      isMounted = false;
      disconnectWebSocket();
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, [authorizedUser]);

  // Auto-scroll to bottom when messages change or during streaming
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamBuffer]);

  const handleSendMessage = () => {
    const trimmed = inputValue.trim();
    if (!trimmed || isStreaming) return;

    if (!activeChatUuid) {
      createNewChatAndSend(trimmed);
    } else {
      sendChatMessage(activeChatUuid, trimmed);
    }

    setInputValue('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleNewChat = () => {
    createNewChat();
  };

  const handleSelectChat = (chatUuid: string) => {
    if (chatUuid === activeChatUuid) return;
    loadChatHistory(chatUuid);
  };

  // Voice dictation using Web Speech API
  const toggleVoice = () => {
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;

    if (!SpeechRecognition) {
      alert('Twoja przeglądarka nie wspiera rozpoznawania mowy.');
      return;
    }

    if (isListening && recognitionRef.current) {
      recognitionRef.current.stop();
      setIsListening(false);
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = 'pl-PL';
    recognition.continuous = false;
    recognition.interimResults = false;

    recognition.onresult = (event: any) => {
      const transcript = event.results[0][0].transcript;
      setInputValue((prev) => (prev ? prev + ' ' + transcript : transcript));
      setIsListening(false);
    };

    recognition.onerror = () => {
      setIsListening(false);
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    recognitionRef.current = recognition;
    recognition.start();
    setIsListening(true);
  };

  const hasMessages = messages.length > 0 || isStreaming;

  return (
    <div className="instructor-ai-page">
      <header className="dashboard__header">
        <SVG type={SVG_TYPE.ADMIN_ANIMATED_ELEMENT} className="dashboard__header-animated-element dashboard__header-animated-element--first" />
        <SVG type={SVG_TYPE.ADMIN_ANIMATED_ELEMENT} className="dashboard__header-animated-element dashboard__header-animated-element--first" />
        <SVG type={SVG_TYPE.ADMIN_ANIMATED_ELEMENT} className="dashboard__header-animated-element dashboard__header-animated-element--second" />

        <div className="dashboard__container">
          <h1 className="dashboard__title">Instruktor AI</h1>
        </div>
      </header>

      <div className="dashboard__content instructor-ai-page__content">
        <div className="instructor-ai-page__layout">
          <aside className="instructor-ai-page__sidebar" aria-label="Panel czatów">
            <button
              type="button"
              className="instructor-ai-page__sidebar-action instructor-ai-page__sidebar-action--active"
              onClick={handleNewChat}
            >
              <SVG type={SVG_TYPE.ROBOT} />
              <span>Nowy czat</span>
            </button>

            {!isSearching ? (
              <button 
                type="button" 
                className="instructor-ai-page__sidebar-action"
                onClick={() => setIsSearching(true)}
              >
                <SVG type={SVG_TYPE.SEARCH_EMPTY} />
                <span>Wyszukaj czaty</span>
              </button>
            ) : (
              <div className="instructor-ai-page__sidebar-search">
                <SVG type={SVG_TYPE.SEARCH_EMPTY} />
                <input
                  type="text"
                  placeholder="Szukaj..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  autoFocus
                  onBlur={() => {
                    if (!searchQuery) setIsSearching(false);
                  }}
                />
                <button
                  type="button"
                  className="instructor-ai-page__sidebar-search-close"
                  onClick={() => {
                    setSearchQuery('');
                    setIsSearching(false);
                  }}
                >
                  &times;
                </button>
              </div>
            )}

            {chats.length > 0 && (
              <div className="instructor-ai-page__sidebar-section">
                <p className="instructor-ai-page__sidebar-label">Twoje czaty</p>
                <div className="instructor-ai-page__sidebar-list">
                  {chats
                    .filter((chat) => 
                      !searchQuery || 
                      (chat.title || 'Nowy czat').toLowerCase().includes(searchQuery.toLowerCase())
                    )
                    .map((chat) => (
                    <button
                      key={chat.uuid}
                      type="button"
                      className={`instructor-ai-page__sidebar-chat ${
                        chat.uuid === activeChatUuid ? 'instructor-ai-page__sidebar-chat--active' : ''
                      }`}
                      onClick={() => handleSelectChat(chat.uuid)}
                    >
                      {chat.title || 'Nowy czat'}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </aside>

          <main className="instructor-ai-page__chat">
            {!hasMessages ? (
              <div className="instructor-ai-page__empty-state">
                <div className="instructor-ai-page__avatar" aria-hidden="true">
                  <img src={robot} alt="Robot" />
                </div>
                <p>Pomogę Ci przygotować się do egzaminu</p>
              </div>
            ) : (
              <div className="instructor-ai-page__messages">
                {messages.map((msg) => (
                  <div
                    key={msg.uuid}
                    className={`instructor-ai-page__message ${
                      msg.role === 'user'
                        ? 'instructor-ai-page__message--user'
                        : 'instructor-ai-page__message--model'
                    }`}
                  >
                    {msg.role !== 'user' && (
                      <div className="instructor-ai-page__message-avatar">
                        <img src={robot} alt="AI" />
                      </div>
                    )}
                    <div className="instructor-ai-page__message-content">
                      {msg.content}
                    </div>
                  </div>
                ))}

                {isStreaming && streamBuffer && (
                  <div className="instructor-ai-page__message instructor-ai-page__message--model">
                    <div className="instructor-ai-page__message-avatar">
                      <img src={robot} alt="AI" />
                    </div>
                    <div className="instructor-ai-page__message-content instructor-ai-page__message-content--streaming">
                      {streamBuffer}
                      <span className="instructor-ai-page__typing-cursor" />
                    </div>
                  </div>
                )}

                {isStreaming && !streamBuffer && (
                  <div className="instructor-ai-page__message instructor-ai-page__message--model">
                    <div className="instructor-ai-page__message-avatar">
                      <img src={robot} alt="AI" />
                    </div>
                    <div className="instructor-ai-page__message-content instructor-ai-page__message-content--streaming">
                      <span className="instructor-ai-page__typing-dots">
                        <span />
                        <span />
                        <span />
                      </span>
                    </div>
                  </div>
                )}

                {error && (
                  <div className="instructor-ai-page__error">
                    {error}
                    <button type="button" onClick={clearError}>✕</button>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>
            )}

            <div className="instructor-ai-page__composer">
              <button type="button" className="instructor-ai-page__composer-icon" aria-label="Dodaj plik">
                <SVG type={SVG_TYPE.ATTACH} />
              </button>
              <input
                type="text"
                className="instructor-ai-page__composer-input"
                placeholder="Zadaj mi pytanie, a spróbuję Ci pomóc"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={isStreaming}
              />
              <button
                type="button"
                className={`instructor-ai-page__composer-icon instructor-ai-page__composer-icon--mic ${
                  isListening ? 'instructor-ai-page__composer-icon--listening' : ''
                }`}
                aria-label="Nagraj wiadomość"
                onClick={toggleVoice}
              >
                <SVG type={SVG_TYPE.MICROPHONE} />
              </button>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}

export default InstructorAIPage;
